import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment, FormAnswerMediaFilterInputObject } from 'graphql-common';
import { ErrorLike } from '@apollo/client';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import Pagination from '@lib/components/Pagination/Pagination';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { ListParams } from '@lib/enums/urls';
import useTaskCompletionsMediaViewer from './utils/useTaskCompletionsMediaViewer';
import styles from './TaskCompletionsMediaGrid.module.scss';

type Props = {
  groupKey: string | null | undefined;
  formAnswerFilters: FormAnswerMediaFilterInputObject[];
  items: Attachment[];
  loading: boolean;
  firstLoading: boolean;
  error?: ErrorLike;
  totalCount: number;
  paginationParams: ListParams;
  setPaginationParams: React.Dispatch<React.SetStateAction<ListParams>>;
};

export default function TaskCompletionsMediaGrid({
  groupKey,
  formAnswerFilters,
  items,
  loading,
  firstLoading,
  error,
  totalCount,
  paginationParams,
  setPaginationParams,
}: Props) {
  const { t } = useTranslation();
  const {
    cacheItems,
    openAttachment,
    close,
    goNext,
    goPrev,
    activeAttachment,
    isNavLoading,
    isPrevDisabled,
    isNextDisabled,
  } = useTaskCompletionsMediaViewer({ groupKey, formAnswerFilters });

  // Every loaded grid page feeds the viewer's id -> attachment cache, so
  // navigating within an already-seen range never needs an extra fetch.
  useEffect(() => {
    cacheItems(items);
  }, [items, cacheItems]);

  if (error) {
    return (
      <ErrorPage description={getDescriptionFromError(error)} status={500} />
    );
  }

  if (firstLoading) {
    return <CircleLoader />;
  }

  if (!items.length) {
    return (
      <ListEmptyState title={t('no-media-yet')} text={t('no-media-text')} />
    );
  }

  const isPaginationVisible = totalCount > items.length;

  return (
    <div className={styles.wrap}>
      <div className={styles.grid} data-loading={loading || undefined}>
        {items.map((attachment) => (
          <DocumentationPreview
            key={attachment.id}
            attachment={attachment}
            view={DocumentationPreviewEnum.BlockCard}
            onClick={() => openAttachment(attachment)}
          />
        ))}
      </div>
      {isPaginationVisible && (
        <Pagination
          paginationState={[paginationParams, setPaginationParams]}
          totalEntries={totalCount}
        />
      )}
      {activeAttachment && (
        <FileViewerModal
          attachment={activeAttachment}
          attachmentName={activeAttachment.filename}
          isOpen
          onClose={close}
          swiperProps={{
            onPrevious: goPrev,
            onNext: goNext,
            isPreviousDisabled: isPrevDisabled || isNavLoading,
            isNextDisabled: isNextDisabled || isNavLoading,
          }}
        />
      )}
    </div>
  );
}
